import { validateResolvedGoalExplosionLoadout, type ResolvedGoalExplosionLoadout } from './GoalExplosionLoadoutContracts';
import type { AudioRouting, GameSfxDefinition, LoopAudioDefinition } from '../audio/GameSfxDefinition';
import type { MatchAudioDefinition } from '../audio/MusicDefinition';
import type {
  GoalExplosionAnimationAudioPreviousBehavior,
  GoalExplosionAudioDefinition,
  GoalExplosionAudioRouting,
} from '../audio/GoalExplosionAudioDefinition';
import type {
  GoalExplosionGameplayEffect,
  GoalExplosionPlacement,
  MatchEffectsDefinition,
} from '../effects/MatchEffectsDefinition';
import type {
  GoalExplosionModelVisual,
  GoalExplosionVfxDefinition,
  GoalExplosionVisualOffset,
} from '../effects/GoalExplosionVisualDefinition';
import type { ArenaPresentation } from '../presentation/ArenaPresentation';
import type { BallPresentation } from '../presentation/BallPresentation';
import { validateFreeplayPresentation, type FreeplayPresentation } from '../presentation/FreeplayPresentation';
import type { TeamPresentation } from '../presentation/TeamPresentation';
import type { VehiclePresentation } from '../presentation/VehiclePresentation';
import type { Team } from '../types';
import { validateGameSfxDefinition } from '../audio/GameSfxDefinition';
import { validateMatchAudioDefinition } from '../audio/MusicDefinition';
import { validateArenaPresentation } from '../presentation/ArenaPresentation';
import { validateBallPresentation } from '../presentation/BallPresentation';
import { validateTeamPresentation } from '../presentation/TeamPresentation';
import { validateVehiclePresentation } from '../presentation/VehiclePresentation';
import { defaultMatchEffectsDefinition, validateMatchEffectsDefinition } from '../effects/MatchEffectsDefinition';
import { validateGoalExplosionVfxDefinition } from '../effects/GoalExplosionVisualDefinition';
import type { LoadingScreenSettingsDto } from '../../loading/LoadingScreenContracts';

export const GAME_CONTENT_VERSION = 3 as const;
export const STORED_GAME_AUDIO_VERSION = 1 as const;
export const GOAL_EXPLOSION_CONFIG_VERSION = 2 as const;

export type MediaAssetKind = 'audio' | 'image' | 'model';

export interface StoredOneShotAudioDefinition {
  readonly revisionIds: readonly string[];
  readonly routing: AudioRouting;
  readonly gain: number;
  readonly gainByIntensity: number;
  readonly playbackRate: number;
  readonly playbackRateByIntensity: number;
}

export interface StoredLoopAudioDefinition {
  readonly revisionId: string;
  readonly routing: AudioRouting;
  readonly gain: number;
  readonly fadeInSeconds: number;
  readonly fadeOutSeconds: number;
  readonly loopStartSeconds?: number;
  readonly loopEndSeconds?: number;
}

export interface StoredGameSfxDefinition {
  readonly engine: Readonly<{
    loop: StoredLoopAudioDefinition | null;
    idleGain: number;
    speedGain: number;
    minPlaybackRate: number;
    maxPlaybackRate: number;
  }>;
  readonly boost: Readonly<{
    start: StoredOneShotAudioDefinition | null;
    loop: StoredLoopAudioDefinition | null;
    release: StoredOneShotAudioDefinition | null;
  }>;
  readonly boostPickupSmall: StoredOneShotAudioDefinition | null;
  readonly boostPickupLarge: StoredOneShotAudioDefinition | null;
  readonly jump: StoredOneShotAudioDefinition | null;
  readonly dodge: StoredOneShotAudioDefinition | null;
  readonly doubleJump: StoredOneShotAudioDefinition | null;
  readonly wheelImpact: StoredOneShotAudioDefinition | null;
  readonly ballImpact: StoredOneShotAudioDefinition | null;
  readonly carImpact: StoredOneShotAudioDefinition | null;
  readonly demolition: StoredOneShotAudioDefinition | null;
  readonly flipReset: StoredOneShotAudioDefinition | null;
  readonly supersonic: Readonly<{
    enter: StoredOneShotAudioDefinition | null;
    loop: StoredLoopAudioDefinition | null;
  }>;
}

export interface StoredGameAudioSettings {
  readonly version: typeof STORED_GAME_AUDIO_VERSION;
  readonly sfx: StoredGameSfxDefinition;
  readonly arenaBackground: Readonly<Record<string, StoredLoopAudioDefinition | null>>;
  readonly chat: Readonly<{
    sendRevisionId: string | null;
    receiveRevisionId: string | null;
  }>;
}

export type StoredGoalExplosionModelVisual = Omit<GoalExplosionModelVisual, 'url'> & Readonly<{ revisionId: string }>;

export interface StoredGoalExplosionVisual {
  readonly duration: number;
  readonly offset: GoalExplosionVisualOffset;
  readonly model: StoredGoalExplosionModelVisual | null;
  readonly vfxPresetRevisionId: string;
  readonly coreColor: number;
}

export interface StoredGoalExplosionAnimationAudioCue {
  readonly animationLayerId: string;
  readonly revisionId: string;
  readonly gain: number;
  readonly delaySeconds?: number;
  readonly previousBehavior: GoalExplosionAnimationAudioPreviousBehavior;
}

export interface StoredGoalExplosionConfig {
  readonly version: typeof GOAL_EXPLOSION_CONFIG_VERSION;
  readonly placement: GoalExplosionPlacement;
  readonly visual: StoredGoalExplosionVisual;
  readonly audio: Readonly<{
    routing: GoalExplosionAudioRouting;
    impact: Readonly<{ revisionId: string; gain: number }> | null;
    ambience: Readonly<{
      revisionId: string;
      gain: number;
      loop: boolean;
      fadeOutSeconds: number;
    }> | null;
    readonly animationCues?: readonly StoredGoalExplosionAnimationAudioCue[];
  }>;
  readonly gameplay: readonly GoalExplosionGameplayEffect[];
}

export interface GoalExplosionVfxPresetRevisionDto {
  readonly id: string;
  readonly presetId: string;
  readonly revision: number;
  readonly value: GoalExplosionVfxDefinition;
  readonly createdAt: string;
}

export interface GoalExplosionVfxPresetDto {
  readonly id: string;
  readonly slug: string;
  readonly name: string;
  readonly archived: boolean;
  readonly latestRevision: number;
  readonly revisions: readonly GoalExplosionVfxPresetRevisionDto[];
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface GameContentSnapshot {
  readonly version: typeof GAME_CONTENT_VERSION;
  readonly revision: string;
  readonly audio: MatchAudioDefinition;
  readonly effects: MatchEffectsDefinition;
  readonly starterGoalExplosionLoadout: ResolvedGoalExplosionLoadout;
  readonly arenaPresentation: ArenaPresentation;
  readonly ballPresentation: BallPresentation;
  readonly teamPresentation: TeamPresentation;
  readonly freeplayPresentation: FreeplayPresentation;
  readonly vehiclePresentations: Readonly<Record<Team, VehiclePresentation>>;
}

export function immutableGameContentSnapshot(value: GameContentSnapshot): GameContentSnapshot {
  const cloned = JSON.parse(JSON.stringify(value)) as GameContentSnapshot;
  validateGameContentSnapshot(cloned);
  return deepFreeze(cloned);
}

export function validateGameContentSnapshot(value: GameContentSnapshot): void {
  if (!value || typeof value !== 'object' || value.version !== GAME_CONTENT_VERSION) {
    throw new Error('Unsupported game content snapshot version.');
  }
  if (typeof value.revision !== 'string' || !value.revision.trim() || value.revision.length > 128) {
    throw new Error('Game content snapshot revision is invalid.');
  }
  validateResolvedGoalExplosionLoadout(value.starterGoalExplosionLoadout);
  validateMatchAudioDefinition(value.audio);
  validateMatchEffectsDefinition(value.effects);
  validateArenaPresentation(value.arenaPresentation);
  validateBallPresentation(value.ballPresentation);
  validateTeamPresentation(value.teamPresentation);
  validateFreeplayPresentation(value.freeplayPresentation);
  if (!value.vehiclePresentations || typeof value.vehiclePresentations !== 'object') {
    throw new Error('Game content vehicle presentations are required.');
  }
  if (!value.vehiclePresentations.blue || !value.vehiclePresentations.orange) {
    throw new Error('Game content vehicle presentations must define both teams.');
  }
  validateVehiclePresentation(value.vehiclePresentations.blue);
  validateVehiclePresentation(value.vehiclePresentations.orange);
}

export interface GoalExplosionCatalogDto {
  readonly id: string;
  readonly slug: string;
  readonly name: string;
  readonly status: 'draft' | 'published' | 'disabled';
  readonly acquisition: 'free' | 'paid';
  readonly priceCents: number;
  readonly revision: number;
  readonly includedInStarterInventory: boolean;
  readonly config: StoredGoalExplosionConfig;
  readonly legal: Readonly<{ notice?: string; license?: string; sourceUrl?: string }>;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface GameAudioSettingsDto {
  readonly revision: number;
  readonly value: StoredGameAudioSettings;
}

export interface StoredGamePresentationSettings {
  readonly freeplay: FreeplayPresentation;
}

export interface GamePresentationSettingsDto {
  readonly revision: number;
  readonly value: StoredGamePresentationSettings;
}

export function validateStoredGamePresentationSettings(value: unknown): asserts value is StoredGamePresentationSettings {
  if (!value || typeof value !== 'object' || Array.isArray(value)
    || Object.keys(value).some(key => key !== 'freeplay') || !('freeplay' in value)) {
    throw new Error('Game presentation settings must define freeplay.');
  }
  validateFreeplayPresentation(value.freeplay);
}

export interface MediaRevisionDto {
  readonly id: string;
  readonly assetId: string;
  readonly assetKey: string;
  readonly kind: MediaAssetKind;
  readonly legal: Readonly<{ notice?: string; license?: string; sourceUrl?: string }>;
  readonly revision: number;
  readonly fileId: string;
  readonly contentType: string;
  readonly sizeBytes: number | null;
  readonly checksumSha256: string;
  readonly metadata: Readonly<Record<string, unknown>>;
  readonly isPublic: boolean;
  readonly createdAt: string;
}

export interface GameContentAdminDto {
  readonly audio: GameAudioSettingsDto;
  readonly presentation: GamePresentationSettingsDto;
  readonly loadingScreens: LoadingScreenSettingsDto;
  readonly goalExplosions: readonly GoalExplosionCatalogDto[];
  readonly vfxPresets: readonly GoalExplosionVfxPresetDto[];
  readonly media: readonly MediaRevisionDto[];
}

export function runtimeLoopFromStored(definition: StoredLoopAudioDefinition, url: string): LoopAudioDefinition {
  return { ...definition, url };
}

export function runtimeSfxFromStored(
  definition: StoredGameSfxDefinition,
  revisionUrl: (revisionId: string) => string,
): GameSfxDefinition {
  const oneShot = (value: StoredOneShotAudioDefinition | null) => value === null ? null : ({
    ...value,
    samples: value.revisionIds.map(revisionUrl),
  });
  const loop = (value: StoredLoopAudioDefinition | null) => value === null ? null : ({
    ...value,
    url: revisionUrl(value.revisionId),
  });
  return {
    engine: { ...definition.engine, loop: loop(definition.engine.loop) },
    boost: {
      start: oneShot(definition.boost.start), loop: loop(definition.boost.loop), release: oneShot(definition.boost.release),
    },
    boostPickupSmall: oneShot(definition.boostPickupSmall),
    boostPickupLarge: oneShot(definition.boostPickupLarge),
    jump: oneShot(definition.jump),
    dodge: oneShot(definition.dodge),
    doubleJump: oneShot(definition.doubleJump),
    wheelImpact: oneShot(definition.wheelImpact),
    ballImpact: oneShot(definition.ballImpact),
    carImpact: oneShot(definition.carImpact),
    demolition: oneShot(definition.demolition),
    flipReset: oneShot(definition.flipReset),
    supersonic: { enter: oneShot(definition.supersonic.enter), loop: loop(definition.supersonic.loop) },
  };
}

export function runtimeGoalExplosionAudioFromStored(
  config: StoredGoalExplosionConfig,
  revisionUrl: (revisionId: string) => string,
): GoalExplosionAudioDefinition {
  const animationLayers = new Map(config.visual.model?.animations.map(layer => [layer.id, layer]) ?? []);
  const animationCues = (config.audio.animationCues ?? []).map((cue) => {
    const layer = animationLayers.get(cue.animationLayerId);
    if (!layer) {
      throw new Error(`Goal explosion animation audio cue ${cue.animationLayerId} references a missing animation layer.`);
    }
    const delaySeconds = cue.delaySeconds ?? 0;
    return {
      animationLayerId: cue.animationLayerId,
      url: revisionUrl(cue.revisionId),
      gain: cue.gain,
      delaySeconds,
      startSeconds: layer.startSeconds + delaySeconds,
      previousBehavior: cue.previousBehavior,
    };
  });
  return {
    routing: config.audio.routing,
    impact: config.audio.impact ? {
      url: revisionUrl(config.audio.impact.revisionId),
      gain: config.audio.impact.gain,
    } : null,
    ambience: config.audio.ambience ? {
      url: revisionUrl(config.audio.ambience.revisionId),
      gain: config.audio.ambience.gain,
      loop: config.audio.ambience.loop,
      fadeOutSeconds: config.audio.ambience.fadeOutSeconds,
    } : null,
    animationCues,
  };
}

export function validateStoredGameAudioSettings(value: StoredGameAudioSettings): void {
  if (value.version !== STORED_GAME_AUDIO_VERSION) throw new Error('Unsupported game audio settings version.');
  validateGameSfxRevisionIds(value.sfx);
  validateGameSfxDefinition(runtimeSfxFromStored(value.sfx, fakeRevisionUrl));
  if (!value.arenaBackground || typeof value.arenaBackground !== 'object' || Array.isArray(value.arenaBackground)) {
    throw new Error('Arena background settings must be an object.');
  }
  for (const [themeId, loop] of Object.entries(value.arenaBackground)) {
    if (!themeId.trim()) throw new Error('Arena theme ID must not be empty.');
    if (loop) {
      revisionId(loop.revisionId, `arena theme ${themeId}`);
      const runtime = runtimeLoopFromStored(loop, fakeRevisionUrl(loop.revisionId));
      if (runtime.routing !== 'global') throw new Error(`Arena theme ${themeId} background must use global routing.`);
    }
  }
  if (!value.chat || typeof value.chat !== 'object') throw new Error('Chat audio settings are required.');
  if (value.chat.sendRevisionId !== null) revisionId(value.chat.sendRevisionId, 'chat send');
  if (value.chat.receiveRevisionId !== null) revisionId(value.chat.receiveRevisionId, 'chat receive');
}

export function validateStoredGoalExplosionConfig(value: StoredGoalExplosionConfig): void {
  if (value.version !== GOAL_EXPLOSION_CONFIG_VERSION) throw new Error('Unsupported goal explosion config version.');
  if (value.visual.model) revisionId(value.visual.model.revisionId, 'goal explosion model');
  revisionId(value.visual.vfxPresetRevisionId, 'goal explosion VFX preset');
  if (value.audio.impact) revisionId(value.audio.impact.revisionId, 'goal explosion impact');
  if (value.audio.ambience) revisionId(value.audio.ambience.revisionId, 'goal explosion ambience');
  for (const cue of value.audio.animationCues ?? []) {
    revisionId(cue.revisionId, `goal explosion animation cue ${cue.animationLayerId}`);
  }
  const defaults = defaultMatchEffectsDefinition();
  validateMatchEffectsDefinition({
    ...defaults,
    goalExplosion: {
      placement: value.placement,
      visual: {
        duration: value.visual.duration,
        offset: value.visual.offset,
        coreColor: value.visual.coreColor,
        vfx: defaults.goalExplosion.visual.vfx,
        model: value.visual.model ? {
          ...value.visual.model,
          url: fakeRevisionUrl(value.visual.model.revisionId),
        } : null,
      },
      audio: runtimeGoalExplosionAudioFromStored(value, fakeRevisionUrl),
      gameplay: value.gameplay,
    },
  });
}

export function validateStoredGoalExplosionVfxPreset(value: GoalExplosionVfxDefinition): void {
  validateGoalExplosionVfxDefinition(value);
}

export function storedAudioRevisionIds(value: StoredGameAudioSettings): readonly string[] {
  const ids = new Set<string>();
  collectGameSfxRevisionIds(value.sfx, ids);
  for (const loop of Object.values(value.arenaBackground)) if (loop) ids.add(loop.revisionId);
  if (value.chat.sendRevisionId) ids.add(value.chat.sendRevisionId);
  if (value.chat.receiveRevisionId) ids.add(value.chat.receiveRevisionId);
  return [...ids];
}

export function storedGoalExplosionRevisionIds(value: StoredGoalExplosionConfig): readonly string[] {
  const ids = new Set<string>();
  if (value.visual.model) ids.add(value.visual.model.revisionId);
  if (value.audio.impact) ids.add(value.audio.impact.revisionId);
  if (value.audio.ambience) ids.add(value.audio.ambience.revisionId);
  for (const cue of value.audio.animationCues ?? []) ids.add(cue.revisionId);
  return [...ids];
}

function validateGameSfxRevisionIds(value: StoredGameSfxDefinition): void {
  const ids = new Set<string>();
  collectGameSfxRevisionIds(value, ids);
  for (const id of ids) revisionId(id, 'game audio');
}

function collectGameSfxRevisionIds(value: StoredGameSfxDefinition, ids: Set<string>): void {
  const oneShots: readonly (StoredOneShotAudioDefinition | null)[] = [
    value.boost.start, value.boost.release, value.boostPickupSmall, value.boostPickupLarge,
    value.jump, value.dodge, value.doubleJump, value.wheelImpact, value.ballImpact, value.carImpact,
    value.demolition, value.flipReset, value.supersonic.enter,
  ];
  for (const item of oneShots) for (const id of item?.revisionIds ?? []) ids.add(id);
  for (const item of [value.engine.loop, value.boost.loop, value.supersonic.loop]) if (item) ids.add(item.revisionId);
}

function revisionId(value: string, label: string): void {
  if (!UUID.test(value)) throw new Error(`${label} revision ID must be a UUID.`);
}

function fakeRevisionUrl(id: string): string {
  return `/api/media/revisions/${id}`;
}

function deepFreeze<T>(value: T): T {
  if (!value || typeof value !== 'object' || Object.isFrozen(value)) return value;
  for (const child of Object.values(value as Record<string, unknown>)) deepFreeze(child);
  return Object.freeze(value);
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
